import type { Bounds } from "../types";
import { destination, linePoint } from "./geo";
import type { Coordinate, SimulationDataset, VisualFeature } from "./types";

const light: VisualFeature = { kind: "traffic_light", signCode: "", signValue: "" };
const crossing: VisualFeature = { kind: "crossing", signCode: "", signValue: "" };
const bump: VisualFeature = { kind: "speed_bump", signCode: "", signValue: "" };
const sign = (code: string, value = ""): VisualFeature => ({ kind: "road_sign", signCode: code, signValue: value });

/** Deliberately fictional landmarks. They are never imported into PostGIS. */
export function createDemoDataset(): SimulationDataset {
    const dataset: SimulationDataset = { id: "control-network-v1", name: "Контрольна мережа · синтетичні дані", source: "demo",
        bounds: [30.46, 50.376, 30.515, 50.43], edges: [], landmarks: [], presets: [], excludedLandmarks: 0, estimatedLandmarks: 0 };
    const pattern = [light, sign("2.1"), crossing, sign("3.29", "50"), bump, sign("2.2"), light, crossing];
    function road(id: string, name: string, origin: Coordinate, features: VisualFeature[], turn: boolean, oneWay: 0 | 1 = 0) {
        let point = origin;
        for (let index = 0; index < features.length; index++) {
            const next = destination(point, turn && index >= 4 ? 180 : 90, 160);
            const edgeId = `${id}-edge-${index}`;
            dataset.edges.push({ id: edgeId, from: `${id}-node-${index}`, to: `${id}-node-${index + 1}`, name, lengthMeters: 160,
                oneWay, geometry: { type: "LineString", coordinates: [point, next] } });
            dataset.landmarks.push({ ...features[index], id: `${id}-mark-${index}`, title: `${name} · ${index + 1}`,
                coordinate: linePoint([point, next], 0.35), verified: true, accuracyMeters: 3, estimatedBinding: false,
                bindings: [{ edgeId, direction: "both", offsetMeters: 56 }] });
            point = next;
        }
        dataset.presets.push({ id, name, fromLandmark: `${id}-mark-0`, toLandmark: `${id}-mark-${features.length - 1}` });
    }
    road("a", "Умовний маршрут А · поворот праворуч", [30.47, 50.393], pattern, true);
    road("b", "Умовний маршрут Б · обмеження 70", [30.47, 50.404], pattern.map((feature, index) => index === 3 ? sign("3.29", "70") : index === 7 ? sign("4.1") : feature), true);
    road("c", "Умовний маршрут В · інший п’ятий об’єкт", [30.47, 50.416], pattern.map((feature, index) => index === 4 ? light : feature), true, 1);
    // A real fork of the simulated graph: the first four landmarks belong to
    // the same road, while an identical suffix continues straight.
    const fork = dataset.edges.find(edge => edge.id === "a-edge-3")!.geometry.coordinates[1] as Coordinate;
    road("d", "Розвилка А · продовжити прямо", fork, pattern.slice(4), false);
    dataset.edges.find(edge => edge.id === "d-edge-0")!.from = "a-node-4";
    dataset.presets.find(preset => preset.id === "d")!.fromLandmark = "a-mark-0";
    const twins = [light, sign("2.3"), crossing, sign("3.29", "40"), bump, crossing, sign("2.2")];
    road("e", "Двійник 1 · неоднозначна послідовність", [30.49, 50.392], twins, true);
    road("f", "Двійник 2 · та сама послідовність", [30.49, 50.415], twins, true);
    const coordinates = dataset.edges.flatMap(edge => edge.geometry.coordinates);
    dataset.bounds = [Math.min(...coordinates.map(p => p[0])) - 0.003, Math.min(...coordinates.map(p => p[1])) - 0.003,
        Math.max(...coordinates.map(p => p[0])) + 0.003, Math.max(...coordinates.map(p => p[1])) + 0.003] as Bounds;
    return dataset;
}
